import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, User } from "lucide-react";
import DOMPurify from "dompurify";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blog/posts/${postId}`)
      .then((r) => setPost(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [postId]);

  if (loading)
    return (
      <div className="pt-28 lg:pt-44 min-h-screen flex items-center justify-center">
        <p className="text-charcoal/40">Loading...</p>
      </div>
    );

  if (!post)
    return (
      <div className="pt-28 lg:pt-44 min-h-screen flex items-center justify-center">
        <p className="text-charcoal/60">Post not found</p>
      </div>
    );

  return (
    <div className="pt-28 lg:pt-44" data-testid="blog-post-page">
      <article className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/high-on-life"
            className="inline-flex items-center gap-2 text-sm text-charcoal/60 hover:text-ocean mb-8 transition-colors"
            data-testid="back-to-blog"
          >
            <ArrowLeft size={16} /> Back to High on Life
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="bg-sage/10 text-sage text-xs font-medium px-3 py-1 rounded-full capitalize">
              {post.category}
            </span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 mb-6">
              {post.title}
            </h1>
            <div className="flex items-center gap-6 text-sm text-charcoal/50 mb-8">
              <span className="flex items-center gap-1">
                <User size={14} /> {post.author}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={14} />{" "}
                {new Date(post.created_at).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
          </motion.div>

          {post.image_url && (
            <motion.img
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              src={post.image_url}
              alt={post.title}
              className="w-full rounded-2xl aspect-[16/9] object-cover mb-12"
            />
          )}

          <div className="prose prose-lg max-w-none text-charcoal leading-relaxed">
            {post.content.split("\n\n").map((paragraph, i) => (
              <p
                key={i}
                className="mb-4"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    paragraph
                      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                      .replace(/\*(.*?)\*/g, "<em>$1</em>")
                  ),
                }}
              />
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-ocean/10 flex flex-wrap gap-2">
            {post.tags?.map((tag) => (
              <span key={tag} className="text-sm text-sage bg-sage/10 px-3 py-1 rounded-full">
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
}
